/**
 * §5.12 (docs/KONZEPT-ANFRAGE-ZU-ANGEBOT.md) — „Aus der Anfrage noch offen".
 *
 * Die Positionsliste eines Angebots beantwortet „was ist drin?". Die Frage, an der ein Angebot
 * scheitert, ist „was fehlt?" — und die stand bisher nirgends. Im Referenzfall verlangte die
 * Anfrage ausdrücklich Getränkepauschale, Technik, Bühnenfläche, Live-Musik und
 * Eisstockschießen; im Angebot kam davon nichts vor, und niemand erfuhr es. Wer dem Ergebnis
 * vertraut, legt den Anfragetext nicht daneben.
 *
 * BENENNEN STATT ZÄHLEN: Die Wünsche stehen einzeln da, in den Worten der Anfrage. „3 Wünsche
 * offen" schickt den Betrieb zurück in den Anfragetext; eine Liste kann er abarbeiten.
 *
 * BESCHREIBEND: Die Karte sagt, was nicht abgebildet wurde. Sie beurteilt weder die Anfrage noch
 * das Angebot und schlägt keine Position vor — welche Position passt, weiß der Betrieb.
 *
 * PUNKT 77, ZUSTAND 2 (21.09.2026): Dazu kommt der Fall dazwischen. Antoine: „Der Gast hat
 * explizit Wasser und Kaffee gewünscht, was in keiner Variante **oder: was nur in einer Variante
 * von drei** berücksichtigt wird." Der erste Fall stand hier schon; der zweite war unsichtbar —
 * ein Wunsch, der in einer von drei Fassungen vorkam, erschien nirgends. Jetzt steht er als
 * eigener Abschnitt da, mit den Namen der Fassungen. Kommt ein Wunsch in ALLEN Fassungen vor,
 * erscheint weiterhin keine Zeile: kein Rauschen.
 *
 * Eigene Datei, damit sie ohne die 3.000-Zeilen-Seite gerendert und geprüft werden kann.
 */
import { Icon, Card } from "./components";

export interface OffenePunkteKarteProps {
  /** Wünsche aus der Anfrage ohne Entsprechung im Katalog. */
  unmatchedRequests: string[];
  /**
   * Punkt 77, Zustand 2: Wünsche, die nur in EINIGEN Fassungen stehen — je Wunsch die Namen
   * dieser Fassungen. Leer, solange kein Angebot mit mehreren Fassungen vorliegt.
   */
  teilweiseBeruecksichtigt?: { wunsch: string; inVarianten: string[] }[];
  /** Wie viele Fassungen das Angebot insgesamt hat — für „nur in 1 von 3". */
  variantenGesamt?: number;
  /** Vom Halluzinations-Guard verworfene Modell-Zeilen (erfundene Katalog-Ids). */
  strippedCount: number;
  /**
   * Punkt 77, das Wegklicken: Punkte, die der Betrieb bewusst nicht aufnimmt. Sie verschwinden
   * aus den Listen oben und stehen stattdessen zusammengefasst am Fuss der Karte - sichtbar
   * genug, um sie zurueckholen zu koennen, leise genug, um nicht im Weg zu stehen.
   */
  weggeklickt?: string[];
  /**
   * Ausblenden bzw. zurueckholen. Fehlt der Rueckruf (etwa in einer reinen Leseansicht), erscheint
   * kein Knopf - die Karte bleibt dann genau das, was sie vorher war.
   */
  onAusblenden?: (wunsch: string, ausgeblendet: boolean) => void;
}

/**
 * „nur in Variante 2 von 3" — Antoines wörtliche Formulierung. Die Fassungen werden benannt,
 * nicht nur gezählt: „nur in Klassisch" sagt dem Betrieb, wo er nachsehen muss; „nur in 1 von 3"
 * allein schickt ihn suchen. Die Zahl steht daneben, weil sie das Verhältnis zeigt.
 */
function fassungsSatz(inVarianten: string[], gesamt: number): string {
  const namen = inVarianten.join(", ");
  return gesamt > 0 ? `nur in ${namen} (${inVarianten.length} von ${gesamt})` : `nur in ${namen}`;
}

/**
 * Punkt 77: der Weg, einen Punkt loszuwerden. Bewusst ein schlichter Textknopf und kein Kreuz:
 * ein Kreuz liest sich wie „loeschen", und geloescht wird hier nichts - der Punkt wandert an den
 * Fuss der Karte und kann zurueckgeholt werden.
 */
function AusblendenKnopf({ wunsch, onAusblenden }: { wunsch: string; onAusblenden: (wunsch: string, ausgeblendet: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onAusblenden(wunsch, true)}
      className="shrink-0 text-[11px] font-medium text-on-surface-variant/70 underline-offset-2 hover:text-primary hover:underline"
      aria-label={`„${wunsch}" ausblenden — bewusst nicht aufgenommen`}
    >
      ausblenden
    </button>
  );
}

export function OffenePunkteKarte({
  unmatchedRequests,
  teilweiseBeruecksichtigt = [],
  variantenGesamt = 0,
  strippedCount,
  weggeklickt = [],
  onAusblenden,
}: OffenePunkteKarteProps) {
  // Der Wortlaut ist der Schluessel (siehe Kopf der Route): was weggeklickt wurde, faellt aus
  // BEIDEN Listen - ein Punkt kann beim naechsten Lauf von „fehlt ueberall" zu „fehlt hier" werden,
  // und die Entscheidung des Betriebs gilt trotzdem weiter.
  const ausgeblendet = new Set(weggeklickt);
  const offen = unmatchedRequests.filter((w) => !ausgeblendet.has(w));
  const teilweise = teilweiseBeruecksichtigt.filter((e) => !ausgeblendet.has(e.wunsch));
  if (offen.length === 0 && teilweise.length === 0 && weggeklickt.length === 0 && strippedCount <= 0) return null;
  return (
    <Card className="space-y-3 p-4 text-beleg-rechenweg">
      {offen.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Icon name="rule" className="shrink-0 text-[13px] text-primary" />
            <span className="font-medium text-on-background dark:text-inverse-on-surface">Im Angebot nicht abgebildet</span>
          </div>
          <ul className="list-disc space-y-1 pl-5 text-on-surface-variant">
            {offen.map((w) => (
              <li key={w} className="flex items-start justify-between gap-2">
                <span>{w}</span>
                {onAusblenden && <AusblendenKnopf wunsch={w} onAusblenden={onAusblenden} />}
              </li>
            ))}
          </ul>
          <p className="text-on-surface-variant/80">
            Aus der Anfrage, ohne passende Katalogposition. Position von Hand ergänzen oder im Anschreiben ansprechen.
          </p>
        </div>
      )}

      {teilweise.length > 0 && (
        <div className="space-y-1" data-testid="teilweise-beruecksichtigt">
          <div className="flex items-center gap-1.5">
            <Icon name="rule_settings" className="shrink-0 text-[13px] text-primary" />
            <span className="font-medium text-on-background dark:text-inverse-on-surface">Nicht in allen Varianten enthalten</span>
          </div>
          <ul className="list-disc space-y-1 pl-5 text-on-surface-variant">
            {teilweise.map((e) => (
              <li key={e.wunsch} className="flex items-start justify-between gap-2">
                <span>
                  {e.wunsch} — <span className="text-on-surface-variant/80">{fassungsSatz(e.inVarianten, variantenGesamt)}</span>
                </span>
                {onAusblenden && <AusblendenKnopf wunsch={e.wunsch} onAusblenden={onAusblenden} />}
              </li>
            ))}
          </ul>
          <p className="text-on-surface-variant/80">
            Aus der Anfrage ausdrücklich gewünscht. Wählt der Gast eine Variante ohne diesen Punkt, fehlt er ihm.
          </p>
        </div>
      )}

      {weggeklickt.length > 0 && (
        <div className="space-y-1 border-t border-outline-variant/30 pt-2" data-testid="weggeklickte-punkte">
          <div className="flex items-center gap-1.5">
            <Icon name="visibility_off" className="shrink-0 text-[13px] text-on-surface-variant/70" />
            <span className="text-on-surface-variant/80">
              {weggeklickt.length === 1 ? "1 Punkt ausgeblendet" : `${weggeklickt.length} Punkte ausgeblendet`}
            </span>
          </div>
          <ul className="space-y-0.5 text-on-surface-variant/70">
            {weggeklickt.map((w) => (
              <li key={w} className="flex items-start justify-between gap-2">
                <span className="line-through">{w}</span>
                {onAusblenden && (
                  <button
                    type="button"
                    onClick={() => onAusblenden(w, false)}
                    className="shrink-0 text-[11px] font-medium text-on-surface-variant/70 underline-offset-2 hover:text-primary hover:underline"
                    aria-label={`„${w}" wieder einblenden`}
                  >
                    zurückholen
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {strippedCount > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Icon name="filter_alt_off" className="shrink-0 text-[13px] text-primary" />
            <span className="font-medium text-on-background dark:text-inverse-on-surface">
              {strippedCount === 1 ? "1 vorgeschlagene Zeile verworfen" : `${strippedCount} vorgeschlagene Zeilen verworfen`}
            </span>
          </div>
          <p className="text-on-surface-variant">
            Sie verwiesen auf Katalogpositionen, die es nicht gibt, und wurden deshalb nicht übernommen.
          </p>
        </div>
      )}
    </Card>
  );
}
